import { Icon } from '@iconify/react/dist/iconify.js';
import { Box, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import NoDataFound from '../../../../components/NodataIndication/NoDataFound';
import Loader3D from '../../../../components/Loader/Loader3D';
import { getOgdTeamColorById } from '../../../../utils/constants/functions';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { getTeamWiseAnalysis } from '../../../../redux/features/poinTrackerSlice';
import { ogdWings } from '../../../../utils/constants/GlobalStaticData';
import dayjs from 'dayjs';
import DatePickerCustom from '../../../../components/DatePicker/DatePickerCustom';
import { restrict2digitAfterDecimal } from '../../../../utils/helperFunction';

const TeamWiseAnalysis = ({teamWiseAnalysisDate,setTeamWiseAnalysisDate}) => {
  const dispatch = useDispatch();
  const teamWiseAnalysis = useSelector((state) => state.pointTracker.teamWiseAnalysis);
  const { responseData, loading } = teamWiseAnalysis;
  const noDatafound = responseData?.data?.length === 0;
  const data = responseData?.data || [];
  const weeks = responseData?.meta?.weeks || ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
  const teams = responseData?.meta?.teams || ogdWings;
  const totals = responseData?.meta?.totals || {};

  const previousDate=useRef(teamWiseAnalysisDate)

  useEffect(() => {
    if(Object.entries(teamWiseAnalysis?.responseData || {}).length === 0 || previousDate.current !== teamWiseAnalysisDate){
    dispatch(getTeamWiseAnalysis({ date: teamWiseAnalysisDate }));
    previousDate.current = teamWiseAnalysisDate
    }
  }, [teamWiseAnalysisDate,dispatch,teamWiseAnalysis?.responseData]);

  const getTeamName = (id) => {
    return teams.find((t) => t.id === id)?.value;
  };

  return (
    <Paper
      sx={{
        borderRadius: '16px',
        overflow: 'hidden',
        border: '1px solid #e5e7eb',
      }}
    >
      <Box
        sx={{
          //   background: 'linear-gradient(90deg, #2563eb, #7c3aed)',
          //   color: '#fff',
          p: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              p: 1,
              borderRadius: '10px',
              background: 'rgba(255,255,255,0.2)',
            }}
          >
            <Icon
              icon="uil:calender"
              width={30}
              style={{ backgroundColor: '#9d25fd', borderRadius: '10px', padding: '5px', color: 'white' }}
            />
          </Box>

          <Box>
            <Typography fontWeight={600}>Team Wise Analysis</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Comprehensive team performance across all weeks
            </Typography>
          </Box>
        </Stack>
        <Box width={'33%'} mt={1}>
          <DatePickerCustom
            label={'Month'}
            value={dayjs(teamWiseAnalysisDate)}
            setValue={(date) => setTeamWiseAnalysisDate(date.format('YYYY-MM-DD'))}
            viewDetails={['year', 'month']}
            fullWidth
            disabled={false}
            height={36}
            fontSize={'0.75rem'}
          />
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', height: '40vh' }}>
          <Loader3D />
        </Box>
      ) : noDatafound ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', height: '40vh' }}>
          <NoDataFound />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ background: '#fff', minWidth: '900px' }}>
            <Table
              size="small"
              sx={{
                tableLayout: 'fixed',
                width: '100%',
                '& th, & td': { border: '1px solid #07070761' },
              }}
            >
              {/* HEADER */}
              <TableHead sx={{ backgroundColor: '#9d25fd' }}>
                <TableRow>
                  <TableCell
                    rowSpan={2}
                    sx={{ fontWeight: 700, width: '160px !important', textAlign: 'center', color: 'white !important' }}
                  >
                    Team
                  </TableCell>

                  {weeks.map((w, index) => (
                    <TableCell
                      sx={{ width: '350px !important', color: 'white !important' }}
                      key={w + index}
                      colSpan={3}
                      align="center"
                    >
                      {w.toUpperCase().split('K').join('K - ')}
                    </TableCell>
                  ))}
                </TableRow>

                <TableRow>
                  {weeks.map((w) => (
                    <React.Fragment key={w}>
                      <TableCell sx={{ color: 'white !important' }}>Revenue</TableCell>
                      <TableCell sx={{ color: 'white !important' }}>Target</TableCell>
                      <TableCell sx={{ color: 'white !important' }}>%</TableCell>
                    </React.Fragment>
                  ))}
                </TableRow>
              </TableHead>

              {/* BODY */}
              <TableBody>
                {data?.map((row) => (
                  <TableRow key={row.id} sx={{ height: 50, backgroundColor: getOgdTeamColorById(row.id).bgColor }}>
                    <TableCell sx={{ color: getOgdTeamColorById(row.id).color, fontWeight: 600 }}>
                      {getTeamName(row.id)}
                    </TableCell>

                    {weeks.map((w) => (
                      <React.Fragment key={w}>
                        <TableCell>{restrict2digitAfterDecimal(row[w]?.revenue)}</TableCell>
                        <TableCell>{row[w]?.target}</TableCell>
                        <TableCell
                          sx={{
                            color: row[w]?.percentage > 25 ? 'green' : row[w]?.percentage < 20 ? 'red' : 'orange',
                            fontWeight: 600,
                          }}
                        >
                          {row[w]?.percentage}%
                        </TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                ))}

                {/* TOTAL ROW */}
                <TableRow sx={{ background: '#1e293b', color: '#fff', height: 50 }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>TOTAL</TableCell>

                  {weeks.map((w) => (
                    <React.Fragment key={w}>
                      <TableCell sx={{ color: '#fff' }}>{totals[w]?.revenue}</TableCell>
                      <TableCell sx={{ color: '#fff' }}>{totals[w]?.target}</TableCell>
                      <TableCell sx={{ color: 'yellow', fontWeight: 700 }}>{totals[w]?.percentage}%</TableCell>
                    </React.Fragment>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default React.memo(TeamWiseAnalysis);
